<template>
  <v-combobox
    v-model="internalModel"
    :items="computedItems"
    :label="label"
    :hint="hint"
    :error="error"
    :error-messages="errorMessage"
    hide-details="auto"
    :custom-value="true"
    multiple
    chips
    clearable
    deletable-chips
    persistent-hint
    hide-selected
    hide-no-data
    class="w-full mt-4"
    auto-grow
    color="primary20"
    theme="light"
    variant="outlined"
    density="comfortable"
    item-title="title"
    item-value="value"
  >
    <template #prepend-item>
      <v-list-item
        class="text-sm text-gray-300"
        :ripple="false"
        :clickable="false"
        density="comfortable"
      >
        <v-list-item-title class="d-flex align-center">
          <v-icon :icon="mdiMagnify" size="24px" class="mr-2"></v-icon>
          <span>Pesquisar ou criar um novo</span>
        </v-list-item-title>
      </v-list-item>
    </template>

    <template #chip="{ item, props }">
      <v-chip
        v-bind="props"
        class="bg-[#E2E2E9] text-black text-sm rounded-md px-1 py-1 border border-gray-500"
      >
        {{ item.title }}
      </v-chip>
    </template>
  </v-combobox>
</template>

<script setup lang="ts">
import { mdiMagnify } from '@mdi/js'
import { ref, watch, computed } from 'vue'

type Item = {
  title: string
  value: number | null
}

const props = defineProps<{
  label?: string
  hint?: string
  modelValue: Item[]
  items: Item[]
  error?: boolean
  errorMessage?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: Item[]): void
}>()

const internalModel = ref<Item[]>([...props.modelValue])

const normalizeItem = (item: Item | string): Item => {
  if (typeof item === 'string') {
    return {
      title: item,
      value: null
    }
  }
  return item
}

watch(
  () => props.modelValue,
  (newVal) => {
    const isDifferent = JSON.stringify(newVal) !== JSON.stringify(internalModel.value)
    if (isDifferent) {
      internalModel.value = [...newVal]
    }
  }
)

watch(internalModel, (newVal) => {
  const normalized = newVal.map(normalizeItem)
  const isDifferent = JSON.stringify(normalized) !== JSON.stringify(props.modelValue)
  if (isDifferent) {
    emit('update:modelValue', normalized)
  }
})

const computedItems = computed<Item[]>(() => {
  const itemsSet = new Map<string | number, Item>()

  props.items.forEach((item) => {
    itemsSet.set(item.value ?? item.title, item)
  })

  internalModel.value.forEach((item) => {
    const key = item.value ?? item.title
    if (!itemsSet.has(key)) {
      itemsSet.set(key, item)
    }
  })

  return Array.from(itemsSet.values())
})
</script>
